import { useState, createContext, useContext } from "react";
import "@/App.css";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";

import ProductDetail from "@/pages/consumer/ProductDetail";
import CartDrawer from "@/components/CartDrawer";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLogin from "@/pages/admin/AdminLogin";
import HomePage from "@/pages/HomePage";
import LegalPage from "@/pages/LegalPage";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function App() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutRequest, setCheckoutRequest] = useState(0);

  const cartKey = (product) =>
    `${product.slug}::${product.selected_variant?.variant_id || 'default'}`;

  const applyVariant = (product, variant) => {
    if (!variant) return product;
    return {
      ...product,
      selected_variant: variant,
      pack_size: variant.pack_size || product.pack_size,
      farmer_price: variant.farmer_price ?? product.farmer_price,
      platform_fee: variant.platform_fee ?? product.platform_fee,
      original_platform_fee: variant.original_platform_fee ?? product.original_platform_fee ?? product.platform_fee,
      platform_fee_discount: variant.platform_fee_discount ?? product.platform_fee_discount ?? 0,
      shipping: variant.shipping ?? product.shipping,
      packaging: variant.packaging ?? variant.handling ?? product.packaging ?? product.handling,
      handling: variant.packaging ?? variant.handling ?? product.packaging ?? product.handling,
      testing: variant.testing ?? product.testing,
      total_price: variant.total_price,
      list_price: variant.list_price,
      sellable_pack_count: variant.sellable_pack_count ?? product.sellable_pack_count,
      pack_base_quantity: variant.pack_base_quantity ?? product.pack_base_quantity,
    };
  };

  const addToCart = (
    product,
    quantity,
    openDrawer = false
  ) => {
    const variants = product.variants || [];
    const selectedVariant =
      product.selected_variant ||
      variants[0] ||
      null;
    const cartProduct = applyVariant(product, selectedVariant);
    const key = cartKey(cartProduct);

    const existing = cart.find(
      item => cartKey(item.product) === key
    );

    if (existing) {
      setCart(
        cart.map(item =>
          cartKey(item.product) === key
            ? {
                ...item,
                quantity: item.quantity + quantity
              }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          product: cartProduct,
          quantity
        }
      ]);
    }

    if (openDrawer) {
      setCartOpen(true);
    }
  };

  const updateCartQuantity = (
    productSlug,
    variantId,
    quantity
  ) => {
    const key = `${productSlug}::${variantId || 'default'}`;

    if (quantity <= 0) {
      setCart(
        cart.filter(
          item => cartKey(item.product) !== key
        )
      );
      return;
    }

    setCart(
      cart.map(item =>
        cartKey(item.product) === key
          ? { ...item, quantity }
          : item
      )
    );
  };

  const updateCartVariant = (productSlug, currentVariantId, nextVariantId) => {
    setCart((currentCart) => {
      const currentKey = `${productSlug}::${currentVariantId || 'default'}`;
      const source = currentCart.find((item) => cartKey(item.product) === currentKey);
      if (!source) return currentCart;

      const nextVariant = (source.product.variants || []).find(
        (variant) => variant.variant_id === nextVariantId
      );
      if (!nextVariant) return currentCart;

      const nextProduct = applyVariant(source.product, nextVariant);
      const nextKey = cartKey(nextProduct);
      const existingTarget = currentCart.find(
        (item) => cartKey(item.product) === nextKey && cartKey(item.product) !== currentKey
      );

      const withoutSource = currentCart.filter(
        (item) => cartKey(item.product) !== currentKey
      );

      if (existingTarget) {
        return withoutSource.map((item) =>
          cartKey(item.product) === nextKey
            ? { ...item, quantity: item.quantity + source.quantity }
            : item
        );
      }

      return [...withoutSource, { ...source, product: nextProduct }];
    });
  };

  const removeFromCart = (productSlug, variantId) => {
    const key = `${productSlug}::${variantId || 'default'}`;
    setCart(
      cart.filter(
        item => cartKey(item.product) !== key
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const openCart = () => {
    setCartOpen(true);
  };

  const beginCheckout = () => {
    setCheckoutRequest((request) => request + 1);
    setCartOpen(true);
  };

  return (
    <AuthContext.Provider
      value={{
        cart,
        addToCart,
        updateCartQuantity,
        updateCartVariant,
        removeFromCart,
        clearCart,
        openCart,
        beginCheckout
      }}
    >

      <div className="app-container">

        <BrowserRouter>

          <Routes>

            <Route path="/" element={<HomePage />} />

            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/shipping-cancellations" element={<LegalPage />} />

            <Route
              path="/browse"
              element={
                <HomePage />
              }
            />

            <Route
              path="/product/:productId"
              element={
                <ProductDetail />
              }
            />

            <Route
              path="/admin/login"
              element={
                <AdminLogin />
              }
            />

            <Route
              path="/admin"
              element={
                <AdminDashboard />
              }
            />

            <Route
              path="*"
              element={
                <Navigate
                  to="/browse"
                  replace
                />
              }
            />

          </Routes>

          <CartDrawer
            open={cartOpen}
            onOpenChange={setCartOpen}
            checkoutRequest={checkoutRequest}
          />

        </BrowserRouter>

        <Toaster
          position="top-center"
          richColors
        />

      </div>

    </AuthContext.Provider>
  );
}

export default App;
